import {
  DeleteOutlined,
  LinkOutlined,
  PlusOutlined,
  TrophyOutlined,
  UploadOutlined,
  UserOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Upload
} from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import { showErrorNotification } from "../../../utils/notification";
import {
  PROFILE_TYPE_UI_CONFIG,
  SOCIAL_PLATFORM_OPTIONS,
  WEBSITE_FEATURED_PROFILE_TYPES
} from "../helper";

const { TextArea } = Input;
const { Item, List, useForm, useWatch } = Form;

const CreateEditProfileModal = ({
  isModalOpen = false,
  initialValues = null,
  setIsModalOpen = () => {},
  onSubmit = () => {},
  profileType,
  ROOT_URL
}) => {
  const [form] = useForm();
  const { entityLabel } = PROFILE_TYPE_UI_CONFIG[profileType];
  const isMentor = profileType === WEBSITE_FEATURED_PROFILE_TYPES.MENTOR;

  const [isUploadingImage, setIsUploadingImage] = useState(false);
  // Set only when the image comes through the upload flow; manual/external
  // URLs clear it so the backend does not keep a stale metadata reference.
  const [uploadedFileMetadataId, setUploadedFileMetadataId] = useState(null);

  const displayImageUrl = useWatch("displayImageUrl", form);

  const uploadProfileImage = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setIsUploadingImage(true);
    axios
      .post(`${ROOT_URL}/admin/website-featured-profiles/${profileType}/upload-asset`, formData)
      .then(({ data: { imageHostedUrl, fileMetadataId } = {} }) => {
        form.setFieldsValue({ displayImageUrl: imageHostedUrl });
        setUploadedFileMetadataId(fileMetadataId);
      })
      .catch((err) => showErrorNotification(err))
      .finally(() => setIsUploadingImage(false));
    return false; // prevent antd's default upload request
  };

  const handleFinish = ({
    socialLinks = [],
    achievements = [],
    displayImageUrl: submittedImageUrl = "",
    ...profileFields
  }) => {
    const payload = {
      ...profileFields,
      displayImageUrl: submittedImageUrl,
      socialLinks: socialLinks.filter(({ platform, url } = {}) => platform && url),
      fileMetadataId: uploadedFileMetadataId
    };
    if (isMentor) {
      payload.achievements = achievements.filter(Boolean);
    }
    onSubmit(payload);
  };

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    if (initialValues) {
      const {
        socialLinks = [],
        achievements = [],
        fileMetadataId = null,
        ...profileFields
      } = initialValues;

      form.setFieldsValue({
        ...profileFields,
        socialLinks,
        ...(isMentor && { achievements })
      });
      setUploadedFileMetadataId(fileMetadataId);
    } else {
      form.resetFields();
      form.setFieldsValue({
        displayOrder: 0,
        socialLinks: [],
        ...(isMentor && { isCurrentMentor: true, achievements: [] })
      });
      setUploadedFileMetadataId(null);
    }
  }, [isModalOpen, initialValues, form, isMentor]);

  const { key: editingProfileId } = initialValues || {};
  const isEditing = Boolean(editingProfileId);

  return (
    <Modal
      title={isEditing ? `Update ${entityLabel}` : `Create New ${entityLabel}`}
      open={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      width={720}
      footer={[
        <Button key="back" onClick={() => setIsModalOpen(false)}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={() => form.submit()}>
          {isEditing ? "Update Changes" : `Create ${entityLabel}`}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Divider orientation="left" style={{ marginTop: 0 }}>
          <UserOutlined /> Profile
        </Divider>

        <Row gutter={16}>
          <Col span={16}>
            <Item label="Name" name="displayName" rules={[{ required: true, max: 200 }]}>
              <Input placeholder="Full name shown on the website" />
            </Item>
          </Col>
          <Col span={8}>
            <Item
              label="Display Order"
              name="displayOrder"
              tooltip="Lower renders first within its section."
            >
              <InputNumber style={{ width: "100%" }} />
            </Item>
          </Col>
        </Row>

        <Item
          label="Platform Account Email"
          name="userEmail"
          tooltip="Email of this person's TLE account. Their institute is pulled from that profile unless you type one below. Leave blank if they have no account."
          rules={[{ type: "email", message: "Must be a valid email" }]}
        >
          <Input placeholder="person@example.com" />
        </Item>

        <Row gutter={16} align="bottom">
          <Col span={16}>
            <Item
              label="Image URL"
              name="displayImageUrl"
              tooltip="Upload an image or paste an external CDN URL."
              rules={[
                { required: true, message: "Image URL is required" },
                { type: "url", message: "Must be a valid URL" }
              ]}
            >
              <Input placeholder="https://..." onChange={() => setUploadedFileMetadataId(null)} />
            </Item>
          </Col>
          <Col span={8}>
            <Item label=" " colon={false}>
              <Upload
                beforeUpload={uploadProfileImage}
                multiple={false}
                showUploadList={false}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />} loading={isUploadingImage}>
                  Upload Image
                </Button>
              </Upload>
            </Item>
          </Col>
        </Row>

        {displayImageUrl && (
          <div style={{ marginBottom: 16, textAlign: "center" }}>
            <img
              src={displayImageUrl}
              alt="Profile preview"
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #ddd"
              }}
            />
          </div>
        )}

        <Divider orientation="left">
          <LinkOutlined /> Social Links
        </Divider>

        <List name="socialLinks">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={8} align="middle">
                  <Col span={7}>
                    <Item
                      {...restField}
                      name={[name, "platform"]}
                      rules={[{ required: true, message: "Platform is required" }]}
                    >
                      <Select placeholder="Platform" options={SOCIAL_PLATFORM_OPTIONS} />
                    </Item>
                  </Col>
                  <Col span={15}>
                    <Item
                      {...restField}
                      name={[name, "url"]}
                      rules={[
                        { required: true, message: "URL is required" },
                        { type: "url", message: "Must be a valid URL" }
                      ]}
                    >
                      <Input placeholder="https://..." />
                    </Item>
                  </Col>
                  <Col span={2}>
                    <Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Item>
                  </Col>
                </Row>
              ))}
              <Item>
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                  Add Social Link
                </Button>
              </Item>
            </>
          )}
        </List>

        {isMentor ? (
          <>
            <Divider orientation="left">
              <TrophyOutlined /> Mentor Details
            </Divider>

            <Row gutter={16}>
              <Col span={16}>
                <Item
                  label="Institute"
                  name="instituteName"
                  tooltip="Leave blank to use the institute on the linked platform account; anything typed here overrides it."
                  rules={[{ max: 200 }]}
                >
                  <Input placeholder="College / university" />
                </Item>
              </Col>
              <Col span={8}>
                <Item
                  label="Current Mentor"
                  name="isCurrentMentor"
                  valuePropName="checked"
                  tooltip="Splits the Current vs Past mentor tabs on the website."
                >
                  <Switch checkedChildren="Current" unCheckedChildren="Past" />
                </Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Item
                  label="Codeforces Max Rating"
                  name={["codeforcesDetails", "maxRating"]}
                  rules={[{ type: "number", min: 0, message: "Must be 0 or more" }]}
                >
                  <InputNumber style={{ width: "100%" }} placeholder="e.g. 2100" />
                </Item>
              </Col>
              <Col span={12}>
                <Item
                  label="Codeforces Max Title"
                  name={["codeforcesDetails", "maxTitle"]}
                  rules={[{ max: 100 }]}
                >
                  <Input placeholder="e.g. master" />
                </Item>
              </Col>
            </Row>

            <Item label="Achievements">
              <List name="achievements">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row key={key} gutter={8} align="middle">
                        <Col span={22}>
                          <Item
                            {...restField}
                            name={name}
                            rules={[{ required: true, message: "Achievement cannot be empty" }]}
                          >
                            <Input placeholder="e.g. ICPC World Finalist" />
                          </Item>
                        </Col>
                        <Col span={2}>
                          <Item>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            />
                          </Item>
                        </Col>
                      </Row>
                    ))}
                    <Item>
                      <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                        Add Achievement
                      </Button>
                    </Item>
                  </>
                )}
              </List>
            </Item>
          </>
        ) : (
          <>
            <Divider orientation="left">Testimonial</Divider>

            <Item
              label="Testimonial Text"
              name="displayTestimonialText"
              rules={[{ required: true, max: 2000 }]}
            >
              <TextArea rows={4} placeholder="The quoted words shown to visitors..." />
            </Item>

            <Item
              label="Attribution"
              name="displayAffiliationText"
              tooltip='Line under the name, e.g. "Google" or "TLE Eliminators 5.0".'
              rules={[{ max: 300 }]}
            >
              <Input placeholder="Role / company / batch" />
            </Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default CreateEditProfileModal;
